import { Checkbox, Col, Form, Input, Row, Select, Space, Tooltip } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../../shared/components/modal/modal';
import { ValidationTrigger } from '../../../../shared/enums/validation-trigger';
import { handleServerFormErrors, mapServerErrors } from '../../../../shared/utils/error-handling/error-handling';
import { filterValueByCriteria } from '../../../../shared/utils/filter-value-by-criteria/filter-value-by-criteria';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import ClientGroupsSelect from '../../../client-groups/components/client-groups-select/client-groups-select';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import {
  useCreateClientUserMutation,
  useGetClientUserQuery,
  useGetClientUsersRolesQuery,
  useUpdateClientUserMutation,
} from '../../api/client-users-api';
import { clientUserFormModalId, clientUsersAlertId } from '../../constants/constants';
import { ClientUserFormModel } from '../../models/client-user-form-model';
import { selectActiveClientUserId, setActiveClientUserId } from '../../store/client-users-slice';
import ClientUsersUnitSelect from '../client-users-unit-select/client-users-unit-select';
import { InfoCircleOutlined } from '@ant-design/icons';

const ClientUserModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['client-users', 'common']);
  const dispatch = useAppDispatch();
  const [emailValidationTrigger, setEmailValidationTrigger] = useState<ValidationTrigger>(ValidationTrigger.OnBlur);

  const activeClientUserId = useAppSelector(selectActiveClientUserId);
  const { data: userRoles } = useGetClientUsersRolesQuery();
  const { data: clientUser } = useGetClientUserQuery(activeClientUserId || '', {
    skip: !activeClientUserId,
  });
  const gutter = 18;

  const [postClientUser, { isLoading: isCreateClientUserLoading }] = useCreateClientUserMutation();
  const [editClientUser, { isLoading: isUpdateClientUserLoading }] = useUpdateClientUserMutation();

  const [form] = Form.useForm<ClientUserFormModel>();

  const closeClientUserFormModal = () => {
    dispatch(closeModal(clientUserFormModalId));
    dispatch(setActiveClientUserId(null));
  };

  const onSubmit = (formValues: ClientUserFormModel) => {
    const payload = {
      name: formValues.clientUserName,
      email: formValues.clientUserEmail,
      viewUnits: formValues.viewUnits.map(unit => ({ unit })),
      unit: formValues.clientUserUnit,
      roles: formValues.role ? [formValues.role] : [],
      username: formValues.username,
      groups: formValues.groups,
      active: formValues.active,
    };

    (activeClientUserId ? editClientUser({ id: activeClientUserId, ...payload }) : postClientUser(payload))
      .unwrap()
      .then(() => {
        closeClientUserFormModal();
        dispatch(
          setAlert({
            id: clientUsersAlertId,
            type: 'success',
            title: activeClientUserId ? t('formUser.messages.editSuccess') : t('formUser.messages.createSuccess'),
          }),
        );
      })
      .catch(error => {
        handleServerFormErrors(
          form,
          mapServerErrors(error.data?.errors, {
            name: 'clientUserName',
            email: 'clientUserEmail',
            unit: 'clientUserUnit',
          }),
        );
      });
  };

  const roleSelectOptions: DefaultOptionType[] = useMemo(() => {
    return (Array.isArray(userRoles) ? userRoles : []).map(roleVal => ({
      label: t(`roles.${roleVal}`),
      value: roleVal,
    }));
  }, [t, userRoles]);

  const initialValues: ClientUserFormModel = useMemo(() => {
    return {
      clientUserName: clientUser?.name || '',
      username: clientUser?.username || '',
      clientUserEmail: clientUser?.email || '',
      viewUnits:
        (clientUser && Array.isArray(clientUser?.viewUnits) ? clientUser.viewUnits : []).map(({ unit }) => unit) || [],
      clientUserUnit: clientUser?.unit || '',
      groups: clientUser?.groups.map(({ id }) => id) || [],
      role: clientUser?.roles[0] || undefined,
      active: clientUser?.active || false,
    };
  }, [clientUser]);

  const submitForm = () => {
    emailValidationTrigger === ValidationTrigger.OnBlur && setEmailValidationTrigger(ValidationTrigger.OnChange);
    form.submit();
  };

  useEffect(() => {
    form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <Modal
      afterClose={afterClose}
      title={activeClientUserId ? t('formUser.editUser') : t('formUser.createUser')}
      modalProps={{
        width: 753,
      }}
      onOk={submitForm}
      onCancel={() => (!isCreateClientUserLoading || !isUpdateClientUserLoading) && closeClientUserFormModal()}
      isLoading={isCreateClientUserLoading || isUpdateClientUserLoading}
      isOpen={isOpen}
    >
      <Form initialValues={initialValues} layout="vertical" form={form} onFinish={onSubmit}>
        <Row gutter={gutter}>
          <Col flex="auto">
            <Form.Item
              name="clientUserName"
              label={t('label.name', { ns: 'common' })}
              rules={[{ required: true, message: t('errors.nameRequired', { ns: 'common' }) }]}
            >
              <Input placeholder={t('label.name', { ns: 'common' })} />
            </Form.Item>
          </Col>
          <Col flex="auto">
            <Form.Item
              name="username"
              label={t('label.username', { ns: 'common' })}
              rules={[{ required: true, message: t('errors.userNameRequired', { ns: 'common' }) }]}
            >
              <Input placeholder={t('label.username', { ns: 'common' })} />
            </Form.Item>
          </Col>
        </Row>
        <div>
          <Form.Item
            name="clientUserEmail"
            label={t('label.email', { ns: 'common' })}
            validateTrigger={emailValidationTrigger}
            rules={[
              {
                type: 'email',
                message: t('errors.emailFormat', { ns: 'common' }),
              },
              { required: true, message: t('errors.emailRequired', { ns: 'common' }) },
            ]}
            normalize={value => value.trim()}
          >
            <Input placeholder={t('label.email', { ns: 'common' })} />
          </Form.Item>
        </div>
        <Form.Item
          name="role"
          label={t('label.role', { ns: 'common' })}
          rules={[{ required: true, message: t('errors.roleRequired', { ns: 'common' }) }]}
        >
          <Select
            allowClear={true}
            showSearch
            options={roleSelectOptions}
            placeholder={t('label.role', { ns: 'common' })}
            filterOption={(inputValue, option) => filterValueByCriteria((option?.label ?? '').toString(), inputValue)}
          />
        </Form.Item>
        <Form.Item name="clientUserUnit" label={t('label.unit', { ns: 'common' })}>
          <Input placeholder={t('label.unit', { ns: 'common' })} />
        </Form.Item>
        <Form.Item name="viewUnits" label={t('label.viewUnits')}>
          <ClientUsersUnitSelect mode="multiple" maxTagCount={3} placeholder={t('label.viewUnits')} />
        </Form.Item>
        <Form.Item label={t('label.groups', { ns: 'common' })} name="groups">
          <ClientGroupsSelect mode="multiple" maxTagCount={4} />
        </Form.Item>
        {clientUser && (
          <Form.Item name="active" valuePropName="checked">
            <Checkbox>
              <Tooltip mouseEnterDelay={0.5} placement="topLeft" title={t('hint.activateUser', { ns: 'common' })}>
                <Space>
                  <span>{t('label.active', { ns: 'common' })}</span>
                  <InfoCircleOutlined />
                </Space>
              </Tooltip>
            </Checkbox>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

const ClientUserFormModal = (): JSX.Element => {
  return <LazyModal modalId={clientUserFormModalId} Component={ClientUserModalContent} />;
};

export default ClientUserFormModal;
